import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Banknote, CalendarDays, CircleCheck, ReceiptText, Wallet } from 'lucide-react';
import { Button, Card, Modal, Spinner, Table } from '../../ui';
import { request } from '../../api/client';
import { usePermissionChecker } from '../../store/auth';
import { OrderLink, RetailerLink } from '../../components/EntityLinks';
import { inr2 } from '../../lib/format';
import { formatIstDate } from '../../lib/time';

/**
 * The two money books a field staff member carries, on their own page.
 *
 * Both figures already existed, one screen each — the wallet float on `/wallets`, the cash they are
 * holding on `/billing/payments?tab=repcash` — and the custodian list already links its rows HERE
 * through `UserLink`. The trip back did not exist: a reader who arrived at "Asmita Kale" from a
 * cash-oversight row had no way to ask the next question ("what else is on this person?") without
 * walking the menu. This card is that return leg.
 *
 * THE TWO NUMBERS ARE NEVER ADDED. A wallet float is company money released to this person to
 * SPEND; custody is cash they collected from retailers and OWE BACK. They point in opposite
 * directions, so a combined "total" would be meaningless — hence two panels, two labels, two
 * destinations, and no sum anywhere on the card.
 */
export function StaffMoneyCard({ repId, isField, repName }: { repId: string; isField: boolean; repName?: string | null }) {
  const navigate = useNavigate();
  /** The per-payment breakdown of the custody figure, shown in place rather than on a page that
   *  cannot filter to one person. */
  const [breakdown, setBreakdown] = useState(false);
  const hasPerm = usePermissionChecker();
  // Each half is gated on the permission that guards the page it opens, so the card never shows a
  // figure whose full ledger the reader would then be bounced out of.
  const canWallet = hasPerm('EXPENSE.VIEW');
  const canCustody = hasPerm('CASH.CUSTODY.VIEW');
  /*
   * A WALLET is a field thing — only a Sales Person or Sales Head is ever given one, and reading a
   * wallet CREATES it, so asking for an Accountant's would mint a row nobody wanted.
   *
   * CUSTODY is not. Cash handed in at the office passes into the receiver's own custody, so an
   * Accounts or Admin person can be holding company notes exactly like a rep — the custodian list
   * says so on its own rows. The question this half asks is "are they holding cash?", never "are
   * they a sales person?", so it is offered to any staff member who turns out to have a movement.
   */
  const showWallet = canWallet && isField;

  const wallet = useQuery({
    queryKey: ['staff-wallet', repId],
    queryFn: () => request<any>({ url: `/wallets/${repId}` }),
    enabled: !!repId && showWallet,
    // A Sales Head only sees their own reporting line, so a wallet outside it answers 403. That is a
    // legitimate answer, not a fault worth three retries and a spinner.
    retry: false,
  });

  /*
   * The DERIVED figure, not the retired custody ledger.
   *
   * Collection money outstanding = approved cash payments this person recorded, minus the company bank
   * deposits allocated against them. Nothing is posted into it and nothing is closed out of it, so it
   * cannot drift from the payments and the bank the way a stored balance could — and it does not stop
   * at field staff: whoever took the money is accountable for it.
   */
  const custody = useQuery({
    queryKey: ['staff-retailer-money', repId],
    queryFn: () => request<{
      collected: number; deposited: number; awaitingConfirmation: number; outstanding: number; payments: any[];
      /* The two ways cash leaves their hands without a bank seeing it. The endpoint has always returned
         both; this card read neither, so it printed "₹1,57,694 collected · ₹1,53,410 deposited" above a
         balance of ₹1,850 — a subtraction the reader cannot make, off by exactly the terms left out. */
      spentOnExpenses: number; movedToWallet: number; spend: any[];
    }>(
      { url: `/retailer-money/staff/${repId}` },
    ),
    enabled: !!repId && canCustody,
    retry: false,
  });

  const walletBal = Number(wallet.data?.currentBalance ?? 0);
  /* Owed to them under the settlement rule — a reimbursement fixed at approval and not yet paid, never a negative
     wallet — and money they owe back on a reversed claim. */
  const walletOwed = Number(wallet.data?.reimbursementOutstanding ?? 0);
  const walletRecovery = Number(wallet.data?.recoveryOutstanding ?? 0);
  const custodyBal = Number(custody.data?.outstanding ?? 0);
  const collected = Number(custody.data?.collected ?? 0);
  const deposited = Number(custody.data?.deposited ?? 0);
  /* Banked on their own say-so and not yet seen in the account — still inside the outstanding figure. */
  const awaitingConfirmation = Number(custody.data?.awaitingConfirmation ?? 0);
  /* The other two terms of the same subtraction: claims they paid out of the cash, and cash turned into
     wallet float for an approved advance. Neither reaches a bank and neither is owed back. */
  const spentOnExpenses = Number(custody.data?.spentOnExpenses ?? 0);
  const movedToWallet = Number(custody.data?.movedToWallet ?? 0);
  const movements: any[] = custody.data?.payments ?? [];
  /** The documents behind those two terms, so the figures can be traced and not just read. */
  const spend: any[] = custody.data?.spend ?? [];
  /* Only the terms that actually apply get a tile — a rep who never spent a rupee of it should not be
     shown a ₹0 column to subtract. Class names are picked from a map: Tailwind cannot see a built one. */
  const tileCount = 3 + (spentOnExpenses > 0.005 ? 1 : 0) + (movedToWallet > 0.005 ? 1 : 0);
  const TILE_COLS: Record<number, string> = { 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4', 5: 'sm:grid-cols-5' };
  /*
   * NO "held for / since" here, deliberately.
   *
   * The obvious hint to add is the age the oversight table chases — and it is the one thing this card
   * must not derive. That column measures from the oldest UNCLEARED collection (the payment date
   * behind it), which is not the same row as the oldest cash-in movement in this ledger: on the very
   * first rep tried, the two anchors were a day apart, so the same words would have carried two
   * different dates on two screens. The age is stated once, on the screen that defines it, one click
   * away. What this card says instead is only what its own rows can prove.
   */
  // Field staff always get the custody half, ₹0 included — "holding nothing" is an answer worth
  // reading about someone who collects cash. A desk role gets it only once they actually hold, or
  // have held, some: an unconditional ₹0 on every Admin's page would be noise, not information.
  const showCustody = canCustody
    && (isField || (!!custody.data && (movements.length > 0 || Math.abs(custodyBal) > 0.005)));

  if (!showWallet && !showCustody) return null;

  return (
    <Card title="Money" className="mb-4">
      {/* Two up when both books apply; a lone panel takes the full width rather than sitting in half
          a card next to an empty column. */}
      <div className={`grid grid-cols-1 gap-3 ${showWallet && showCustody ? 'sm:grid-cols-2' : ''}`}>
        {showWallet && (
          <Panel
            icon={<Wallet className="size-4" />}
            tint="bg-brand-600/10 text-brand-700 dark:bg-brand-400/10 dark:text-brand-400"
            label="Wallet float"
            loading={wallet.isLoading}
            denied={wallet.isError}
            value={inr2(walletBal)}
            /* Same reading the wallets list puts under this figure: positive is float still out with
               them, negative is money the company owes them back. */
            valueCls={walletBal > 0 ? 'text-brand-700 dark:text-brand-400' : walletBal < 0 ? 'text-danger-600 dark:text-danger-500' : 'text-subtle'}
            note={[
              walletBal > 0 ? 'Float outstanding — company money to spend' : walletBal < 0 ? 'Reimbursement due to them' : 'No float with them',
              walletOwed > 0.005 ? `${inr2(walletOwed)} reimbursement still to pay them` : null,
              walletRecovery > 0.005 ? `${inr2(walletRecovery)} owed back to the company` : null,
            ].filter(Boolean).join(' · ')}
            hint={wallet.data?.lastTxnAt ? `Last entry ${formatIstDate(wallet.data.lastTxnAt)}` : 'No entries yet'}
            cta="Open wallet ledger"
            onOpen={() => navigate(`/wallets?view=advances&focus=${repId}`)}
          />
        )}
        {showCustody && (
          <Panel
            icon={<Banknote className="size-4" />}
            tint="bg-warning-500/10 text-warning-600 dark:text-harvest-300"
            label="Collection money outstanding"
            loading={custody.isLoading}
            denied={custody.isError}
            value={inr2(custodyBal)}
            /* The custody colour the rep's own panel already uses: amber while they are holding
               company notes, muted once everything has reached an account. */
            valueCls={custodyBal > 0.005 ? 'text-warning-600 dark:text-harvest-300' : 'text-subtle'}
            note={custodyBal <= 0.005
              ? 'Everything they collected has reached the company'
              : awaitingConfirmation > 0.005
                /* Chasing somebody for money they have already banked is the fastest way to lose their
                   trust in the figure, so the panel says which half of it is waiting on US. */
                ? `${inr2(awaitingConfirmation)} of this is banked and waiting on Accounts`
                : 'Not yet deposited to company'}
            /* The subtraction, so the headline figure can be checked rather than trusted — WHOLE, or it
               cannot be checked at all. Naming only the first two terms turned a line meant to prove the
               balance into one that contradicts it. */
            hint={collected > 0.005
              ? [
                `${inr2(collected)} collected`,
                `${inr2(deposited)} deposited`,
                spentOnExpenses > 0.005 ? `${inr2(spentOnExpenses)} spent on claims` : null,
                movedToWallet > 0.005 ? `${inr2(movedToWallet)} moved to wallet` : null,
              ].filter(Boolean).join(' · ')
              : 'No cash has passed through them'}
            /*
             * THE ROWS, NOT A LIST OF EVERYBODY'S.
             *
             * This opened `/billing/payments?mode=CASH&receivedBy=<repId>` — but `receivedBy` is
             * honoured by nothing: PaymentsListPage reads only tab/status/mode/bank/q/retailer. So a
             * button under one person's figure produced EVERY custodian's cash, and "₹200 collected,
             * ₹0 deposited" became a number you could not trace to anyone. The breakdown was already
             * in hand — `/retailer-money/staff/:repId` returns the payments that make up the figure —
             * so it is shown here instead of navigating somewhere that cannot answer the question.
             */
            cta="See the payments behind it"
            onOpen={() => setBreakdown(true)}
          />
        )}
      </div>

      {breakdown && (
        <Modal
          open
          onOpenChange={(o) => { if (!o) setBreakdown(false); }}
          title={`Collection money · ${repName ?? 'this person'}`}
          width={860}
          footer={<Button variant="default" onClick={() => setBreakdown(false)}>Close</Button>}
        >
          <p className="mb-3 type-body text-muted">
            Every cash payment {repName ?? 'this person'} recorded, and how much of each has reached a
            company account.
          </p>

          {/* THE SUBTRACTION, AS TILES — every term of it. The same sentence as a run of plain text made
              the reader parse arithmetic; as collected → out → what is left, the colour carries the
              meaning: neutral in, green banked, muted gone-but-not-banked, amber still in a pocket.
              Two tiles were missing, which is worse than unhelpful: the row read collected − deposited
              and did not come to the balance printed beside it. */}
          <div className={`mb-4 grid grid-cols-2 gap-2 ${TILE_COLS[tileCount] ?? 'sm:grid-cols-3'}`}>
            <div className="rounded-control border border-line bg-surface-2 px-3 py-2">
              <div className="type-overline text-subtle">Collected</div>
              <div className="mt-0.5 type-metric-sm tabular-nums text-content">{inr2(collected)}</div>
            </div>
            <div className="rounded-control border border-brand-500/25 bg-brand-500/8 px-3 py-2">
              <div className="type-overline text-brand-700 dark:text-brand-300">Reached the company</div>
              <div className="mt-0.5 type-metric-sm tabular-nums text-brand-700 dark:text-brand-300">{inr2(deposited)}</div>
            </div>
            {spentOnExpenses > 0.005 && (
              <div className="rounded-control border border-line bg-surface-2 px-3 py-2">
                <div className="type-overline text-subtle">Spent on approved claims</div>
                <div className="mt-0.5 type-metric-sm tabular-nums text-muted">{inr2(spentOnExpenses)}</div>
                <div className="mt-0.5 type-caption text-subtle">never banked · not owed back</div>
              </div>
            )}
            {movedToWallet > 0.005 && (
              <div className="rounded-control border border-line bg-surface-2 px-3 py-2">
                <div className="type-overline text-subtle">Moved into their wallet</div>
                <div className="mt-0.5 type-metric-sm tabular-nums text-muted">{inr2(movedToWallet)}</div>
                <div className="mt-0.5 type-caption text-subtle">company float now</div>
              </div>
            )}
            <div className={custodyBal > 0.005
              ? 'rounded-control border border-warning-500/30 bg-warning-500/10 px-3 py-2'
              : 'rounded-control border border-line bg-surface-2 px-3 py-2'}>
              <div className={`type-overline ${custodyBal > 0.005 ? 'text-warning-600 dark:text-harvest-300' : 'text-subtle'}`}>Still with them</div>
              <div className={`mt-0.5 type-metric-sm tabular-nums ${custodyBal > 0.005 ? 'text-warning-600 dark:text-harvest-300' : 'text-subtle'}`}>{inr2(custodyBal)}</div>
              {awaitingConfirmation > 0.005 && (
                <div className="mt-0.5 type-caption text-subtle">{inr2(awaitingConfirmation)} banked, awaiting Accounts</div>
              )}
            </div>
          </div>
          <Table
            rowKey="id"
            size="small"
            dataSource={movements}
            pagination={movements.length > 12 ? { pageSize: 12 } : false}
            locale={{ emptyText: 'No cash has passed through them' }}
            columns={[
              {
                // A receipt mark, the way a document number is marked everywhere else in the app.
                title: 'Payment', dataIndex: 'paymentNo',
                render: (v: string) => (
                  <span className="inline-flex items-center gap-1.5 tabular-nums">
                    <ReceiptText size={13} className="shrink-0 text-violet-500" />{v}
                  </span>
                ),
              },
              {
                title: 'Date', dataIndex: 'paymentDate',
                render: (v: string) => (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-pill bg-surface-3 px-2 py-0.5 type-label text-muted">
                    <CalendarDays size={11} className="text-subtle" />{formatIstDate(v)}
                  </span>
                ),
              },
              {
                // WHO the money came from, with the retailer's own steady colour and a link to them —
                // "who collected this" is only half the question; the other half is who paid it.
                title: 'From', dataIndex: 'retailerName',
                render: (v: string, r: any) => (v ? (
                  // No mark: it was a Store glyph repeated on every row — a constant glyph is not
                  // identity (see the colour-sweep brief).
                  <div className="flex min-w-0 items-baseline gap-1.5">
                    <RetailerLink id={r.retailerId} className="truncate">{v}</RetailerLink>
                    {r.retailerCode && <span className="shrink-0 type-caption text-subtle">{r.retailerCode}</span>}
                  </div>
                ) : <span className="text-subtle">—</span>),
              },
              {
                title: 'Order', dataIndex: 'orderNo',
                render: (v: string, r: any) => (v
                  ? <OrderLink id={r.orderId} className="tabular-nums">{v}</OrderLink>
                  : <span className="text-subtle">—</span>),
              },
              { title: 'Collected', dataIndex: 'amount', align: 'right' as const, render: (v: any) => <b className="tabular-nums">{inr2(v)}</b> },
              {
                title: 'Deposited', dataIndex: 'deposited', align: 'right' as const,
                render: (v: any) => (Number(v) > 0.005
                  ? <span className="tabular-nums text-brand-700 dark:text-brand-300">{inr2(v)}</span>
                  : <span className="type-caption text-subtle">none yet</span>),
              },
              {
                title: 'Still with them', dataIndex: 'outstanding', align: 'right' as const,
                render: (v: any, r: any) => {
                  const out = Number(v ?? 0);
                  const waiting = Number(r.awaitingConfirmation ?? 0);
                  if (out <= 0.005) {
                    return (
                      <span className="inline-flex items-center gap-1 type-label text-brand-700 dark:text-brand-300">
                        <CircleCheck size={12} /> cleared
                      </span>
                    );
                  }
                  return (
                    <div>
                      <b className="tabular-nums text-warning-600 dark:text-harvest-300">{inr2(out)}</b>
                      {waiting > 0.005 && <div className="type-caption text-subtle">{inr2(waiting)} banked, awaiting Accounts</div>}
                    </div>
                  );
                },
              },
            ] as any}
          />

          {/*
            * THE CASH THAT LEFT WITHOUT A DEPOSIT.
            *
            * The payments table above is the money coming IN. Two legs take it out again with no bank
            * line to point at, and until this list existed they were totals on another screen: a reader
            * who clicked "₹2,434 spent on approved claims" on the oversight table arrived HERE, where
            * the figure was not mentioned at all. Same rows, same order, one document each.
            */}
          {spend.length > 0 && (
            <div className="mt-5">
              <div className="mb-2 type-overline text-subtle">Cash that left without reaching a bank</div>
              <Table
                rowKey={(r: any, i?: number) => `${r.source}-${r.refId}-${i}`}
                size="small"
                dataSource={spend}
                pagination={spend.length > 12 ? { pageSize: 12 } : false}
                columns={[
                  {
                    title: 'Document', dataIndex: 'refNo',
                    render: (v: string, r: any) => (v
                      ? (
                        <span className="inline-flex items-center gap-1.5 tabular-nums">
                          <ReceiptText size={13} className="shrink-0 text-violet-500" />
                          {r.leg === 'CLAIM'
                            ? <button type="button" className="text-brand-700 hover:underline dark:text-brand-300" onClick={() => navigate(`/expense-reports/${r.refId}`)}>{v}</button>
                            : v}
                        </span>
                      )
                      : <span className="type-caption text-subtle">document no longer on file</span>),
                  },
                  {
                    title: 'Approved', dataIndex: 'happenedAt',
                    render: (v: string) => (v
                      ? (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-pill bg-surface-3 px-2 py-0.5 type-label text-muted">
                          <CalendarDays size={11} className="text-subtle" />{formatIstDate(v)}
                        </span>
                      )
                      : <span className="text-subtle">—</span>),
                  },
                  {
                    // Which of the two legs, in the same words the oversight table uses for its columns.
                    title: 'Where it went', dataIndex: 'leg',
                    render: (v: string, r: any) => (
                      <div className="min-w-0">
                        <div className="truncate type-label text-content">
                          {v === 'CLAIM' ? 'Spent on an approved claim' : 'Moved into their wallet'}
                        </div>
                        {r.settlementNo
                          ? <div className="type-caption tabular-nums text-subtle">{r.settlementNo}</div>
                          : r.source === 'LEGACY' ? <div className="type-caption text-subtle">pre-settlement claim</div> : null}
                      </div>
                    ),
                  },
                  {
                    title: 'Cash used', dataIndex: 'amount', align: 'right' as const,
                    render: (v: any) => <b className="tabular-nums text-content">{inr2(v)}</b>,
                  },
                ] as any}
              />
            </div>
          )}
        </Modal>
      )}
      {/* The warning against adding them up only has something to warn about when both are here. */}
      {showWallet && showCustody && (
        <p className="mt-3 type-caption text-muted">
          Two separate things: a float the company released to them to spend, and retailer cash they
          collected that has not reached the company yet. They are never added together.
        </p>
      )}
    </Card>
  );
}

function Panel({
  icon, tint, label, value, valueCls, note, hint, cta, onOpen, loading, denied,
}: {
  icon: React.ReactNode; tint: string; label: string; value: string; valueCls: string;
  note: string; hint: string; cta: string; onOpen: () => void; loading?: boolean; denied?: boolean;
}) {
  return (
    <div className="rounded-control border border-line bg-surface-2/40 p-3.5">
      <div className="flex items-center gap-2.5">
        <span className={`flex size-7 shrink-0 items-center justify-center rounded-lg ${tint}`}>{icon}</span>
        <span className="type-overline text-content">{label}</span>
      </div>
      {loading ? (
        <div className="mt-3 flex items-center gap-2 py-1.5 text-muted"><Spinner /> Loading…</div>
      ) : denied ? (
        // A 403 here means the reader is outside this person's reporting line — say that, rather
        // than showing a zero that reads as "they hold nothing".
        <p className="mt-3 type-body text-subtle">Not visible to you — this person is outside your team.</p>
      ) : (
        <>
          <div className={`mt-2.5 type-metric tabular-nums ${valueCls}`}>{value}</div>
          <div className="mt-0.5 type-body text-muted">{note}</div>
          <div className="mt-1 type-caption text-subtle">{hint}</div>
          <button
            type="button"
            onClick={onOpen}
            className="mt-3 inline-flex items-center gap-1.5 type-label-strong text-brand-700 transition-colors hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300"
          >
            {cta}
            <ArrowRight className="size-3.5 shrink-0" />
          </button>
        </>
      )}
    </div>
  );
}
